import React, { useState } from "react"
import { IListing, IMediaItem } from "../../lib"
import { ILinkFunc } from "../../lib/util";
import FeaturedWithCarouselContent, { IFeaturedWithCarouselContentProps, ISlideContent } from "../Blades/FeaturedWithCarouselContent";
import TabbedContentBlade, { ITabbedContentBladeProps, ITabContent } from "../Blades/TabbedContentBlade";
import TwoColumnTextLayoutBlade, { EColumnSizing, ITwoColumnLayoutProps } from "../Blades/TwoColumnTextLayoutBlade";
import { Carousel, ECarouselLinkPosition, ICarouselProps } from "../Carousel";
import { EListingCardLayouts, EListingVitalsLayout, IListingCardProps, ListingCard } from "../Listing";
import { MarketStats, MarketStatsProps } from "../MarketStats";
import { EntrySearch, IEntrySearchProps, ISearchContext } from "../Search";

export interface IPageTabContent {
    listings: IListing[];
    tabFilter?: any;
    communities?: {
        header: string;
        image: IMediaItem;
        headline: string;
        content: string;
        callToAction?: { label: string; href: string };
    };
    developments?: {
        headline: string;
        subheadline?: string;
        text: string;
        carouselItems?: ISlideContent[];
    };
    marketStatsJSON?: string;
    marketStatsCTA?: { label: string; href: string }[];
    carouselBottomLinkUrl?: string;
    carouselBottomLinkText?: string;
}

export interface IPageCommunityTab {
    label: string;
    content: IPageTabContent;
}

export interface IPageLayoutTabsProps {
    wrapperClassName?: string;
    baseStandardFilters?: (context: ISearchContext) => React.ReactNode;
    baseMiddleFilters?: (context: ISearchContext) => React.ReactNode;
    tabsContent: IPageCommunityTab[];
    linkFunc?: ILinkFunc;
    heroCarouselProps?: Partial<ICarouselProps>;
    heroCarouselCardProps?: Partial<IListingCardProps>;
    tabsProps?: Partial<ITabbedContentBladeProps>;
    baseEntrySearchProps?: Partial<IEntrySearchProps>;
    communityBladeProps?: Partial<ITwoColumnLayoutProps>;
    developmentsBladeProps?: Partial<ITwoColumnLayoutProps>;
    developmentsCarouselProps?: Partial<IFeaturedWithCarouselContentProps>;
    listingCarouselProps?: Partial<ICarouselProps>;
    listingCarouselCardsProps?: Partial<IListingCardProps>;
    marketStatsProps?: Partial<MarketStatsProps>;
}

export const PageLayoutTabs: React.FC<IPageLayoutTabsProps> = ({
    wrapperClassName = "",
    baseStandardFilters,
    baseMiddleFilters,
    tabsContent,
    heroCarouselProps,
    heroCarouselCardProps,
    baseEntrySearchProps,
    tabsProps,
    linkFunc,
    communityBladeProps,
    developmentsBladeProps,
    developmentsCarouselProps,
    listingCarouselProps,
    listingCarouselCardsProps,
    marketStatsProps,
}) => {

    const linkf = linkFunc || ((props) => <a {...props} />);

    const tabs: ITabContent[] = tabsContent.map((tab) => {
        const standardFilter = (context: ISearchContext) => {
            return (
                <>
                    {baseStandardFilters && baseStandardFilters(context)}
                </>
            )
        }
        return {
            tabTitle: tab.label,
            content: (
                <EntrySearch
                    standardFilters={standardFilter}
                    orFilters={baseMiddleFilters}
                    className="bg-body-bg-inverted lg:bg-transparent"
                    {...baseEntrySearchProps}
                    onSubmit={async filters => {
                        if (tab.content?.tabFilter) {
                            filters = {
                                ...tab.content.tabFilter,
                                ...filters,
                            }
                        }
                        baseEntrySearchProps?.onSubmit &&
                            await baseEntrySearchProps.onSubmit(filters)
                    }}
                />
            )
        }
    })

    const onTabChange = (index: any) => setActiveTab(tabsContent[index])

    const staticTabbedContent = (
        <div className="relative lg:absolute lg:bottom-0 lg:inset-x-0 w-full lg:bg-gradient-to-b lg:from-transparent lg:via-black/60 lg:to-black">
            <TabbedContentBlade
                className="w-full lg:w-auto max-w-screen-2xl mx-auto"
                tabListClassName="lg:px-0 bg-body-bg-inverted lg:bg-transparent"
                tabListItemClassNames={{ default: "text-body-text-inverted border-b", active: "text-body-text-inverted border-b-4 font-bold" }}
                tabs={tabs}
                onTabChange={onTabChange}
                {...tabsProps}
            />
        </div>
    )

    const [activeTab, setActiveTab] = useState(tabsContent[0])

    const marketStats = activeTab.content?.marketStatsJSON ?
        JSON.parse(activeTab.content.marketStatsJSON).markets : null

    let carouselCTA: Partial<ICarouselProps> = {};
    if (activeTab.content.carouselBottomLinkUrl) {
        carouselCTA = {
            link: activeTab.content.carouselBottomLinkUrl,
            linkLabel: activeTab.content.carouselBottomLinkText,
            linkPosition: ECarouselLinkPosition.bottom,
            linkFunc: linkf,
        }
    }

    return (
        <div className={`tabs-page ${wrapperClassName} w-full relative`}>
            {activeTab.content?.listings && activeTab.content.listings.length > 0 && (
                <Carousel
                    className="hero-carousel text-center lg:max-h-screen w-full"
                    staticOverlayChildren={staticTabbedContent}
                    {...heroCarouselProps}
                >
                    {activeTab.content.listings.map((listing, index) => {
                        return (
                            <ListingCard
                                key={index}
                                listing={listing}
                                layout={EListingCardLayouts.composite}
                                vitalsLayout={EListingVitalsLayout.column2Border1}
                                vitalsClassName="md:left-auto md:top-auto md:right-0 md:bottom-1/4 md:bg-none md:pt-[0.75rem] md:bg-body-bg-inverted"
                                maxWidthImage={1000}
                                imageProps={{
                                    lazy: true,
                                    imgProps: {
                                        className:
                                            "w-full h-auto lg:h-full lg:object-cover lg:object-center",
                                        wrapperClassName: "w-full lg:h-screen lg:max-h-screen",
                                        wrapperProps: {
                                            style: {
                                                display: "block"
                                            }
                                        }
                                    }
                                }}
                                linkFunc={linkf}
                                vitalsLocationIcon={false}
                                displayFlags={false}
                                {...heroCarouselCardProps}
                            ></ListingCard>
                        )
                    })}
                </Carousel>
            )}
            {activeTab.content?.communities && (
                <TwoColumnTextLayoutBlade
                    className="mx-auto max-w-screen-2xl"
                    titleHeadingLevel="h2"
                    title={activeTab.content.communities.header}
                    columnSizing={EColumnSizing.left60}
                    leftColumn={{
                        image: activeTab.content.communities.image,
                    }}
                    rightColumn={{
                        titleHeadingLevel: "h3",
                        title: activeTab.content.communities.headline,
                        text: activeTab.content.communities.content,
                        link: { label: activeTab.content.communities.callToAction?.label, url: activeTab.content.communities.callToAction?.href }
                    }}
                    {...communityBladeProps}
                />
            )}
            {activeTab.content?.developments && (
                <TwoColumnTextLayoutBlade
                    className="mx-auto max-w-screen-2xl"
                    columnSizing={EColumnSizing.left40}
                    leftColumn={{
                        titleHeadingLevel: "h3",
                        title: activeTab.content.developments.headline,
                        text: activeTab.content.developments.subheadline
                    }}
                    rightColumn={{
                        text: activeTab.content.developments.text,
                    }}
                    {...developmentsBladeProps}
                />
            )}
            {activeTab.content?.developments?.carouselItems && (
                <FeaturedWithCarouselContent
                    className="mx-auto max-w-screen-2xl"
                    slides={activeTab.content.developments?.carouselItems}
                    {...developmentsCarouselProps}
                />
            )}
            {activeTab.content?.listings && activeTab.content.listings.length > 2 && (
                <Carousel
                    title="Listings"
                    className={`slick-center-zoom`}                    
                    {...carouselCTA}
                    {...listingCarouselProps}
                    slickSettings={{
                        infinite: true,
                        centerMode: true,
                        slidesToShow: 1,
                        arrows: true,
                        slidesToScroll: 1,
                        ...listingCarouselProps?.slickSettings
                    }}
                >
                    {activeTab.content.listings.map((listing, index) => {
                        return (
                            <ListingCard
                                layout={EListingCardLayouts.vertical}
                                vitalsLayout={EListingVitalsLayout.column2Border1}
                                listing={listing}
                                key={index}
                                keyIndex={index}
                                linkFunc={linkf}
                                linkText={"Learn More"}
                                {...listingCarouselCardsProps}
                            />
                        );
                    })}
                </Carousel>
            )}
            {marketStats && (
                <MarketStats
                    className="mx-auto max-w-screen-2xl inverted"
                    title="Market Stats"
                    markets={marketStats}
                    linkAsButton={true}
                    links={activeTab.content.marketStatsCTA}
                    linkFunc={linkf}
                    {...marketStatsProps}
                />
            )}
        </div>
    )
}

export default PageLayoutTabs